import React, { useEffect } from "react";

import { ITag, ITagMinimal } from "@Components/groups/GroupUtils";
import GroupsListSkeleton from "@Components/groups/GroupsListSkeleton";
import GroupListUserItem from "@Components/groups/GroupsListUserItem";
import { GroupContext, UserContext } from "@Constants";
import { IAddressDetailsPaged } from "@Models/IAddressDetails";
import { ICreditDetailsPaged } from "@Models/ICreditDetails";
import { IRoleAssignmentPaged } from "@Models/IRoleDetails";
import { IUserDetailsPaged } from "@Models/IUserDetails";
import { hasCapability, isNil } from "@Utils";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Skeleton } from "antd";
import { ILoadNextPage } from "@Components/shared/types";
import { IAddAction } from "./types";

type IProps = {
    users: IUserDetailsPaged[];
    addAction: IAddAction;
    addressAssignments: IAddressDetailsPaged[];
    addressTags: ITag[];
    addressTagsLoading: string[];
    creditAssignments: ICreditDetailsPaged[];
    creditTags: ITag[];
    creditTagsLoading: string[];
    groupId: string;
    groupItems: JSX.Element[];
    handleUserSelect: (userId: string) => void;
    hasNextPage: boolean;
    isNextPageLoading: boolean;
    licenseTags: ITag[];
    licenseTagsLoading: string[];
    loadNextPage: ILoadNextPage;
    roleAssignments: IRoleAssignmentPaged[];
    roleTags: ITag[];
    roleTagsLoading: string[];
    statusTags: ITagMinimal[];
    removeAction?: (userId: string) => void;
};

const InfiniteUserList: React.FC<IProps> = ({
    users,
    addAction,
    addressAssignments,
    addressTags,
    addressTagsLoading,
    creditAssignments,
    creditTags,
    creditTagsLoading,
    groupId,
    groupItems,
    handleUserSelect,
    hasNextPage,
    isNextPageLoading,
    licenseTags,
    licenseTagsLoading,
    loadNextPage,
    roleAssignments,
    roleTags,
    roleTagsLoading,
    statusTags,
    removeAction,
}: IProps) => {
    const parentRef = React.useRef();
    const totalLength = groupItems.length + users.length;
    const isItemLoaded = (index) => !hasNextPage || index < users.length;

    const rowVirtualizer = useVirtualizer({
        count: hasNextPage ? totalLength + 1 : totalLength,
        getScrollElement: () => parentRef.current,
        estimateSize: () => 60,
        overscan: 5,
    });

    const canAddToGroups = hasCapability("UserGroup/AddMembers", GroupContext, groupId);

    const tagsLoading = (user: string) => {
        return (
            addressTagsLoading.includes(user) ||
            licenseTagsLoading.includes(user) ||
            creditTagsLoading.includes(user) ||
            roleTagsLoading.includes(user)
        );
    };

    useEffect(() => {
        const [lastItem] = [...rowVirtualizer.getVirtualItems()].reverse();
        if (!lastItem) {
            return;
        }
        if (lastItem.index >= totalLength - 1 && hasNextPage && !isNextPageLoading) {
            loadNextPage();
        }
    }, [hasNextPage, loadNextPage, totalLength, isNextPageLoading, rowVirtualizer.getVirtualItems()]);

    const RenderItem = (index: number) => {
        if (index < groupItems.length) {
            return <div>{groupItems[index]}</div>;
        } else if (index > totalLength - 1) {
            return <GroupsListSkeleton even={index % 2 == 0} />;
        }
        const userIndex = index - groupItems.length;
        const user = users?.[userIndex];
        if (isNil(user)) return null;

        return !isItemLoaded(userIndex) ? (
            <Skeleton />
        ) : (
            <GroupListUserItem
                isActive={user?.isActive ?? false}
                group={groupId}
                page={user.page}
                key={"group-" + groupId + "-user-" + user.userId}
                fullName={user.fullName}
                userId={user.userId}
                index={userIndex}
                fetching={false}
                select={handleUserSelect}
                removeAction={removeAction}
                addAction={canAddToGroups ? addAction : null}
                roleTags={roleTags.filter((x) => x.userId === users[userIndex].userId)}
                addressTags={addressTags.filter((x) => x.userId === users[userIndex].userId)}
                licenseTags={licenseTags.filter((x) => x.userId === users[userIndex].userId)}
                creditTags={creditTags.filter((x) => x.userId === users[userIndex].userId)}
                statusTags={statusTags.filter((x) => x.userId === users[userIndex].userId)}
                creditAssignments={creditAssignments.filter(
                    (x) => x.contextId === UserContext && x.instanceId === users[userIndex].userId
                )}
                roleAssignments={roleAssignments.filter((x) => x.userId === users[userIndex].userId)}
                tagsLoading={tagsLoading(users[userIndex].userId)}
            />
        );
    };

    return (
        <div
            ref={parentRef}
            className="List"
            style={{
                height: `100%`,
                width: `100%`,
                overflowY: "scroll",
                overflowX: "hidden",
            }}
        >
            <div
                style={{
                    height: `${rowVirtualizer.getTotalSize()}px`,
                    width: "100%",
                    position: "relative",
                }}
            >
                {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                    return (
                        <div
                            key={virtualRow.index}
                            className={virtualRow.index % 2 ? "list-item-odd" : "list-item-even"}
                            style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: `${virtualRow.size}px`,
                                transform: `translateY(${virtualRow.start}px)`,
                            }}
                        >
                            {RenderItem(virtualRow.index)}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
export default InfiniteUserList;
